import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  STATUS_COLORS,
  SURFACE,
  BORDER,
  TEXT,
  ACCENT,
} from "../constants/colors";
import PriorityBadge from "./PriorityBadge";
import StatusDot from "./StatusDot";
import { formatCurrency } from "../utils/formatCurrency";
import { APP_LOCALE } from "../../../utils/formatDate";

// Stages where dragging is not allowed
const DISABLED_DRAG_STAGES = ["receiving", "in_stock"];

const PO_STAGES = [
  "po_issued",
  "supplier_confirmed",
  "inventory_ready",
  "in_transit",
  "receiving",
  "in_stock",
];
const ORDER_STAGES = ["fulfillment", "pending_delivery"];

function setNavFilter(stageId, card) {
  if (stageId === "backlog") {
    localStorage.setItem(
      "inventory-filter-data",
      JSON.stringify({ filters: [{ key: "sku", values: [card.sku] }] }),
    );
    return "/inventory?child";
  }
  if (PO_STAGES.includes(stageId)) {
    localStorage.setItem(
      "po-filter-data",
      JSON.stringify({
        filters: [{ key: "asn_no", values: [card.uuid] }],
      }),
    );
    return "/purchase-order?child";
  }
  if (ORDER_STAGES.includes(stageId)) {
    localStorage.setItem(
      "order-filter-data",
      JSON.stringify({
        filters: [
          { key: "order_id", values: [card.secondary_uuid || card.uuid] },
        ],
      }),
    );
    return "/order-management?child";
  }
  return null;
}

const KanbanCard = ({
  card,
  onDragStart,
  stage,
  isNew = false,
  isRemoving = false,
  isMoving = false,
  onAddEvent,
}) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const isBlocked = false;
  const isAtRisk = false;
  const isDragDisabled = DISABLED_DRAG_STAGES.includes(stage?.id);

  const handleCardClick = () => {
    const path = setNavFilter(stage?.id, card);
    if (path) navigate(path);
  };

  return (
    <div
      className={`${isNew ? "kanban-card-new" : ""} ${isRemoving ? "kanban-card-removing" : ""} ${isMoving ? "kanban-card-moving" : ""}`}
      draggable={!isDragDisabled}
      onDragStart={(e) => {
        if (isDragDisabled) {
          e.preventDefault();
          return;
        }
        onDragStart(e, card._id);
      }}
      onClick={handleCardClick}
      style={{
        background: isBlocked ? SURFACE.cardBlockedBg : SURFACE.cardBgDefault,
        border: `1px solid ${isBlocked ? `${ACCENT.red}30` : isAtRisk ? `${ACCENT.amber}25` : BORDER.subtle}`,
        borderLeft: `3px solid ${STATUS_COLORS[card.status]}`,
        borderRadius: 8,
        padding: "10px 12px",
        cursor: isDragDisabled ? "default" : "grab",
        transition: "all 0.15s ease",
        position: "relative",
        userSelect: "none",
        opacity: isDragDisabled ? 0.85 : isRemoving ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (isDragDisabled) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 4px 20px ${stage.color}20`;
      }}
      onMouseLeave={(e) => {
        if (isDragDisabled) return;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: TEXT.muted,
            fontFamily: "Poppins",
            letterSpacing: 0.5,
            wordBreak: "break-all",
            minWidth: 0,
          }}
        >
          {card.uuid}
        </span>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <StatusDot status={card.status} />
          <PriorityBadge priority={card.priority} />
          {/* Expand / collapse toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((x) => !x);
            }}
            title={expanded ? "Collapse" : "Expand"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              padding: 0,
              border: `1px solid ${BORDER.subtle}`,
              borderRadius: 3,
              background: expanded ? `${ACCENT.indigo}20` : "transparent",
              color: expanded ? ACCENT.indigoLight : TEXT.muted,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <svg width="8" height="8" viewBox="0 0 10 6" fill="none">
              <path
                d={expanded ? "M1 5l4-4 4 4" : "M1 1l4 4 4-4"}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Title */}
      {PO_STAGES.includes(stage?.id) ? (
        // PO cards: name === uuid (asn_no), show useful date details instead
        <div style={{ marginBottom: 5 }}>
          {card.trail_date && (
            <div
              style={{
                fontSize: 10,
                color: TEXT.muted,
                fontFamily: "Poppins",
              }}
            >
              Updated:{" "}
              <span style={{ color: TEXT.secondary, fontWeight: 600 }}>
                {new Date(card.trail_date).toLocaleDateString(APP_LOCALE, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          )}
          {card.shipment_date && (
            <div
              style={{
                fontSize: 10,
                color: ACCENT.amber,
                fontFamily: "Poppins",
                marginTop: 2,
              }}
            >
              Ship:{" "}
              <span style={{ fontWeight: 600 }}>
                {new Date(card.shipment_date).toLocaleDateString(APP_LOCALE, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: TEXT.primary,
            lineHeight: 1.3,
            marginBottom: 5,
            fontFamily: "Poppins",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {card.name}
        </div>
      )}

      {/* SKU + supplier */}
      <div
        style={{
          fontSize: 10,
          color: TEXT.muted,
          fontFamily: "Poppins",
          marginBottom: 7,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {card.sku_list
          ? `${card.sku_list.length} SKU${card.sku_list.length !== 1 ? "s" : ""}`
          : card.line_items
            ? `${card.line_items.length} item${card.line_items.length !== 1 ? "s" : ""}`
            : `${card.sku || ""}${card.variant_title ? " · " + card.variant_title : ""}`}
      </div>

      {/* Metrics row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <span style={{ fontSize: 11, color: TEXT.secondary }}>
            <span style={{ color: TEXT.tertiary, fontSize: 10 }}>QTY </span>
            <span style={{ fontWeight: 600, fontFamily: "Poppins" }}>
              {(card.qty || 0).toLocaleString()}
            </span>
          </span>
          <span
            style={{
              fontSize: 11,
              color: ACCENT.green,
              fontWeight: 700,
              fontFamily: "Poppins",
            }}
          >
            {formatCurrency(card.total_value ?? card.variant_price)}
          </span>
        </div>
        {/* <CycleTimePip days={card.daysInStage} stageId={card.stage} /> */}
      </div>

      {/* Add Event button — in_transit only */}
      {stage?.id === "in_transit" && (
        <div style={{ marginTop: 6, textAlign: "right" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onAddEvent) onAddEvent(card);
            }}
            style={{
              fontSize: 9,
              padding: "2px 8px",
              border: "1px solid rgba(59,130,246,0.4)",
              borderRadius: 3,
              background: "rgba(59,130,246,0.1)",
              color: "#3b82f6",
              cursor: "pointer",
              fontFamily: "Poppins",
              letterSpacing: 0.5,
            }}
          >
            + Add Event
          </button>
        </div>
      )}

      {/* Stock burn bar (in_stock only) */}
      {card.stage === "in_stock" && card.stockLevel !== undefined && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <span
              style={{
                fontSize: 9,
                color: TEXT.muted,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Stock Level
            </span>
            <span
              style={{
                fontSize: 9,
                color: card.stockLevel < 50 ? ACCENT.amber : ACCENT.green,
                fontFamily: "Poppins",
                fontWeight: 700,
              }}
            >
              {card.stockLevel}%
            </span>
          </div>
          <div
            style={{ height: 3, background: SURFACE.barBg, borderRadius: 2 }}
          >
            <div
              style={{
                height: "100%",
                width: `${card.stockLevel}%`,
                background: card.stockLevel < 50 ? ACCENT.amber : ACCENT.green,
                borderRadius: 2,
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>
      )}

      {/* Expanded: SKU list (ASN stages) */}
      {expanded && card.sku_list && card.sku_list.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: "6px 8px",
            background: "rgba(99,102,241,0.06)",
            border: "1px solid rgba(99,102,241,0.15)",
            borderRadius: 4,
          }}
        >
          {card.sku_list.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10,
                color: TEXT.secondary,
                fontFamily: "Poppins",
                padding: "2px 0",
                borderBottom:
                  idx < card.sku_list.length - 1
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "none",
              }}
            >
              <span style={{ color: TEXT.muted, wordBreak: "break-all" }}>
                {item.sku}
                {item.status ? (
                  <span
                    style={{
                      marginLeft: 4,
                      fontSize: 9,
                      color: ACCENT.indigo,
                    }}
                  >
                    [{item.status}]
                  </span>
                ) : null}
              </span>
              <span style={{ fontWeight: 600 }}>
                ×{" "}
                {item.quantity_accepted ??
                  item.quantity_requested ??
                  item.quantity ??
                  0}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Expanded: line items (order stages) */}
      {expanded && card.line_items && card.line_items.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: "6px 8px",
            background: "rgba(76,175,80,0.06)",
            border: "1px solid rgba(76,175,80,0.15)",
            borderRadius: 4,
          }}
        >
          {card.line_items.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10,
                color: TEXT.secondary,
                fontFamily: "Poppins",
                padding: "2px 0",
                borderBottom:
                  idx < card.line_items.length - 1
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "none",
              }}
            >
              <span style={{ color: TEXT.muted, wordBreak: "break-all" }}>
                {item.sku}
              </span>
              <span style={{ fontWeight: 600 }}>
                × {item.quantity || 0}
                {item.price
                  ? ` · ${formatCurrency(item.price * (item.quantity || 0))}`
                  : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Blocker note */}
      {/* {isBlocked && card.blockerNote && (
        <div
          style={{
            marginTop: 8,
            padding: "5px 8px",
            background: `${ACCENT.red}15`,
            border: `1px solid ${ACCENT.red}30`,
            borderRadius: 4,
            fontSize: 10,
            color: ACCENT.redLight,
            lineHeight: 1.4,
          }}
        >
          🔴 {card.blockerNote}
        </div>
      )} */}

      {/* ETA */}
      {/* {card.eta && card.eta !== "Done" && (
        <div style={{ marginTop: 6, fontSize: 10, color: TEXT.tertiary }}>
          <span style={{ color: TEXT.muted }}>ETA </span>
          <span
            style={{
              color: card.eta === "Today" ? ACCENT.orange : TEXT.secondary,
              fontFamily: "monospace",
              fontWeight: 600,
            }}
          >
            {card.eta}
          </span>
        </div>
      )} */}

      {/* Tags */}
      {/* {card.tags.length > 0 && (
        <div
          style={{ marginTop: 7, display: "flex", flexWrap: "wrap", gap: 3 }}
        >
          {card.tags.map((t) => (
            <span
              key={t}
              style={{
                fontSize: 9,
                padding: "1px 5px",
                borderRadius: 3,
                background:
                  t === "BLOCKED"
                    ? `${ACCENT.red}20`
                    : t === "Rush"
                      ? `${ACCENT.orange}20`
                      : BORDER.subtle,
                color:
                  t === "BLOCKED"
                    ? ACCENT.red
                    : t === "Rush"
                      ? ACCENT.orange
                      : TEXT.muted,
                fontFamily: "monospace",
                letterSpacing: 0.5,
                border: `1px solid ${t === "BLOCKED" ? `${ACCENT.red}30` : BORDER.subtle}`,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      )} */}
    </div>
  );
};

export default KanbanCard;
